import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wrench, GoogleLogo } from "@phosphor-icons/react";

function formatError(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => (x?.msg ? x.msg : JSON.stringify(x))).join(" ");
  return err?.message || "Erro inesperado";
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Bem-vindo!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    const redirect = encodeURIComponent(window.location.origin + "/auth/callback");
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] grid grid-cols-1 lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#001E50] text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #00B0F0 1px, transparent 2px)", backgroundSize: "24px 24px" }} />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-[#00B0F0] flex items-center justify-center">
              <Wrench size={22} weight="fill" className="text-[#001E50]" />
            </div>
            <div>
              <div className="font-heading text-xl">Oficina</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Gestão Profissional</div>
            </div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl xl:text-5xl leading-tight mb-4">
            Sua oficina, <br /><span className="text-[#00B0F0]">no controle.</span>
          </h1>
          <p className="text-white/70 max-w-md text-sm">
            Ordens de serviço, estoque, financeiro e notas fiscais em um só lugar.
            Ambiente seguro com login individual.
          </p>
        </div>
        <div className="relative text-xs uppercase tracking-[0.2em] text-white/40">v1.0 · Volkswagen Blue</div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md animate-fade-up">
          <div className="mb-8 lg:hidden text-center">
            <div className="inline-flex items-center gap-2">
              <div className="w-9 h-9 rounded-md bg-[#001E50] flex items-center justify-center">
                <Wrench size={16} weight="fill" className="text-[#00B0F0]" />
              </div>
              <span className="font-heading text-lg text-[#001E50]">Oficina</span>
            </div>
          </div>

          <div className="text-[11px] uppercase tracking-[0.2em] text-[#475569] mb-1">Acesso ao sistema</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-[#0F172A] tracking-tight mb-8">Entrar</h2>

          <Button
            type="button"
            variant="outline"
            onClick={onGoogle}
            className="w-full mb-4 h-11 border-[#E2E8F0]"
            data-testid="google-btn"
          >
            <GoogleLogo size={18} weight="bold" className="mr-2 text-[#EA4335]" />
            Entrar com Google
          </Button>

          <div className="flex items-center gap-3 my-6 text-xs text-[#475569]">
            <div className="flex-1 h-px bg-[#E2E8F0]" />
            OU COM E-MAIL
            <div className="flex-1 h-px bg-[#E2E8F0]" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label>E-mail</Label>
              <Input
                data-testid="login-email"
                type="email"
                autoComplete="email"
                placeholder="admin@oficina.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Senha</Label>
                <Link to="/forgot-password" className="text-xs text-[#00B0F0] hover:underline" data-testid="forgot-link">
                  Esqueci a senha
                </Link>
              </div>
              <Input
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              data-testid="login-btn"
              disabled={loading}
              className="w-full h-11 bg-[#001E50] hover:bg-[#001740] text-white"
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
